import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { useAuth } from '../contexts/AuthContext';
import { getCampaigns, createCampaign, updateCampaign, startCampaign, retryCampaignFailed, deleteCampaign, getCampaignProgress, refreshCampaignStatus, getMessageLimits, uploadCampaignAttachment, deleteCampaignAttachment, getCampaignAttachment, scheduleCampaign, cancelCampaignSchedule, makeCampaignRecurring, updateCampaignRecurrence, toggleCampaignRecurrence, cancelCampaignRecurrence, getCampaignInstances } from '../lib/api';
import { getListCache, setListCache } from '../lib/listCache';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Play, Trash2, Loader2, Megaphone, Users, Search, X, Edit, Eye, CheckCircle2, XCircle, Clock, Calendar, CircleX, Pencil, MessageCircle, AlertCircle, RotateCcw, Repeat, Pause, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import EmojiPicker from '../components/EmojiPicker';
import FileAttachment from '../components/FileAttachment';

// ===== Seletor visual de data e hora (modo "Uma vez" do agendamento) =====
const CAL_WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const CAL_MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const _pad2 = (n) => String(n).padStart(2, '0');
// Date -> "YYYY-MM-DDTHH:mm" (horário LOCAL, mesmo formato do input datetime-local)
const _toLocalDTStr = (d) => `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}T${_pad2(d.getHours())}:${_pad2(d.getMinutes())}`;

function TimeDial({ value, onUp, onDown, ariaLabel }) {
  return (
    <div className="flex flex-col items-center">
      <button type="button" aria-label={`aumentar ${ariaLabel}`} onClick={onUp}
        className="h-6 w-11 rounded-md hover:bg-secondary flex items-center justify-center text-muted-foreground">
        <ChevronUp className="w-4 h-4" />
      </button>
      <div className="text-3xl font-bold tabular-nums w-14 text-center py-0.5">{_pad2(value)}</div>
      <button type="button" aria-label={`diminuir ${ariaLabel}`} onClick={onDown}
        className="h-6 w-11 rounded-md hover:bg-secondary flex items-center justify-center text-muted-foreground">
        <ChevronDown className="w-4 h-4" />
      </button>
    </div>
  );
}

function ScheduleDateTimePicker({ value, onChange, footer }) {
  const parsed = value ? new Date(value) : null;
  const valid = parsed && !Number.isNaN(parsed.getTime());
  const sel = valid ? parsed : new Date();
  const [view, setView] = useState({ y: sel.getFullYear(), m: sel.getMonth() });

  const now = new Date();
  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startWeekday = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();

  // Sempre 42 células (6 semanas) → altura constante do calendário, sem saltos entre meses.
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length < 42) cells.push(null);

  const selMid = valid ? new Date(sel.getFullYear(), sel.getMonth(), sel.getDate()) : null;

  const pickDay = (d) => {
    const nd = new Date(view.y, view.m, d, valid ? sel.getHours() : 9, valid ? sel.getMinutes() : 0, 0, 0);
    onChange(_toLocalDTStr(nd));
  };
  const bumpMonth = (delta) => {
    const m = view.m + delta;
    setView({ y: view.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 });
  };
  const bumpTime = (unit, delta) => {
    const d = valid ? new Date(sel) : new Date(view.y, view.m, now.getDate(), 9, 0);
    if (unit === 'h') d.setHours((d.getHours() + delta + 24) % 24);
    else d.setMinutes((d.getMinutes() + delta + 60) % 60);
    onChange(_toLocalDTStr(d));
  };

  return (
    <div className="flex flex-col flex-1 justify-between gap-2">
      {/* Calendário */}
      <div className="rounded-xl border bg-card p-2">
        <div className="flex items-center justify-between mb-1">
          <button type="button" onClick={() => bumpMonth(-1)}
            className="h-7 w-7 rounded-md hover:bg-secondary flex items-center justify-center">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold">{CAL_MONTHS[view.m]} {view.y}</span>
          <button type="button" onClick={() => bumpMonth(1)}
            className="h-7 w-7 rounded-md hover:bg-secondary flex items-center justify-center">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-muted-foreground mb-1">
          {CAL_WEEKDAYS.map((w, i) => <div key={i}>{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map((d, i) => {
            if (d === null) return <div key={i} className="h-6" />;
            const cellDate = new Date(view.y, view.m, d);
            const isPast = cellDate < todayMid;
            const isToday = cellDate.getTime() === todayMid.getTime();
            const isSel = selMid && cellDate.getTime() === selMid.getTime();
            return (
              <button
                key={i}
                type="button"
                disabled={isPast}
                onClick={() => pickDay(d)}
                className={`h-6 rounded-md text-[13px] flex items-center justify-center transition
                  ${isSel ? 'bg-primary text-primary-foreground font-semibold shadow'
                    : isPast ? 'text-muted-foreground/40 cursor-not-allowed'
                    : 'hover:bg-secondary'}
                  ${isToday && !isSel ? 'ring-1 ring-primary/50' : ''}`}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      {/* Dial de hora */}
      <div className="rounded-xl border bg-card p-2 flex items-center justify-center gap-2">
        <Clock className="w-5 h-5 text-muted-foreground mr-1" />
        <TimeDial ariaLabel="hora" value={valid ? sel.getHours() : 9} onUp={() => bumpTime('h', 1)} onDown={() => bumpTime('h', -1)} />
        <span className="text-3xl font-bold text-muted-foreground pb-1">:</span>
        <TimeDial ariaLabel="minuto" value={valid ? sel.getMinutes() : 0} onUp={() => bumpTime('m', 1)} onDown={() => bumpTime('m', -1)} />
      </div>

      {footer}
    </div>
  );
}

export default function CampaignsPage() {
  const { canEdit, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Cache SWR: pinta a lista na hora ao reentrar (sem spinner nas revisitas) e
  // revalida em background via fetchCampaigns. NÃO toca na mecânica (pollings,
  // banner, contagem, conclusão) — só o paint inicial da lista.
  const _initCampaigns = getListCache('campaigns');
  const [campaigns, setCampaigns] = useState(_initCampaigns?.value || []);
  const [loading, setLoading] = useState(!_initCampaigns);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [loadingPercent, setLoadingPercent] = useState(0);
  const loadingTimerRef = useRef(null);
  const percentTimerRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [viewingCampaign, setViewingCampaign] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [formData, setFormData] = useState({ name: '', message_template: '' });
  const [submitting, setSubmitting] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [startDialogOpen, setStartDialogOpen] = useState(false);
  const [campaignToStart, setCampaignToStart] = useState(null);
  const [warningPulsing, setWarningPulsing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [runningCampaign, setRunningCampaign] = useState(null);
  const [campaignProgress, setCampaignProgress] = useState(null);
  const [campaignCompleted, setCampaignCompleted] = useState(false);
  const [bannerFading, setBannerFading] = useState(false);
  const textareaRef = useRef(null);
  const progressIntervalRef = useRef(null);
  
  // Plano ilimitado e anexo
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Agendamento
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [campaignToSchedule, setCampaignToSchedule] = useState(null);
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [scheduleConfirmDialogOpen, setScheduleConfirmDialogOpen] = useState(false);
  const [scheduleWarningPulsing, setScheduleWarningPulsing] = useState(false);
  const [notifyByWhatsApp, setNotifyByWhatsApp] = useState(false);
  const [notifyWhatsAppNumber, setNotifyWhatsAppNumber] = useState('');
  // Recorrência (Schedule Dialog, modo "Recorrente")
  const [scheduleMode, setScheduleMode] = useState('once'); // 'once' | 'recurring'
  const [recurFreq, setRecurFreq] = useState('daily');       // daily|weekly|monthly|every_n_days
  const [recurTime, setRecurTime] = useState('09:00');
  const [recurWeekday, setRecurWeekday] = useState('0');     // 0=segunda … 6=domingo
  const [recurDayOfMonth, setRecurDayOfMonth] = useState('1');
  const [recurIntervalDays, setRecurIntervalDays] = useState('3');
  const [recurEndMode, setRecurEndMode] = useState('never'); // never|on_date|count
  const [recurEndDate, setRecurEndDate] = useState('');
  const [recurMaxRuns, setRecurMaxRuns] = useState('10');
  const [creatingRecurring, setCreatingRecurring] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(true); // painel de agendamentos recorrentes (topo)
  const recurringScrollRef = useRef(null); // rolagem das linhas de agendamentos (setas ↑↓)
  // Fase D — "Ver envios": modal read-only com as instâncias já disparadas por um molde
  const [instancesMolde, setInstancesMolde] = useState(null);
  const [instances, setInstances] = useState([]);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const instancesScrollRef = useRef(null); // rolagem por setas do modal "Ver envios" (igual ao painel)
  
  // Filtros
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sentByFilter, setSentByFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  // Efeito para animação de loading
  useEffect(() => {
    if (loading) {
      setLoadingSeconds(0);
      setLoadingPercent(0);
      
      loadingTimerRef.current = setInterval(() => {
        setLoadingSeconds(prev => prev + 1);
      }, 1000);
      
      // Porcentagem animada - vai de 0 a 95 de forma suave
      percentTimerRef.current = setInterval(() => {
        setLoadingPercent(prev => {
          if (prev >= 95) return 95;
          const increment = Math.max(0.5, (95 - prev) / 20);
          return Math.min(95, prev + increment);
        });
      }, 100);
    } else {
      setLoadingPercent(100);
      
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (percentTimerRef.current) {
        clearInterval(percentTimerRef.current);
        percentTimerRef.current = null;
      }
      setLoadingSeconds(0);
    }
    
    return () => {
      if (loadingTimerRef.current) {
        clearInterval(loadingTimerRef.current);
      }
      if (percentTimerRef.current) {
        clearInterval(percentTimerRef.current);
      }
    };
  }, [loading]);

  const getLoadingMessage = () => {
    if (loadingSeconds < 60) {
      return 'Carregando campanhas...';
    } else if (loadingSeconds < 120) {
      return 'Lista grande, aguarde...';
    } else {
      return 'Carregando muitas campanhas, aguarde...';
    }
  };
  const [availableUsers, setAvailableUsers] = useState([]);

  // Função para tocar som de aviso "warning/erro"
  const playWarningSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Criar reverb simples com delay
      const createReverb = () => {
        const convolver = audioContext.createConvolver();
        const rate = audioContext.sampleRate;
        const length = rate * 0.7; // 0.7 segundos de reverb
        const impulse = audioContext.createBuffer(2, length, rate);
        
        for (let channel = 0; channel < 2; channel++) {
          const channelData = impulse.getChannelData(channel);
          for (let i = 0; i < length; i++) {
            channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2.5);
          }
        }
        convolver.buffer = impulse;
        return convolver;
      };
      
      const reverb = createReverb();
      const reverbGain = audioContext.createGain();
      reverbGain.gain.value = 0.5; // Intensidade do reverb
      
      reverb.connect(reverbGain);
      reverbGain.connect(audioContext.destination);
      
      // Som de erro - 2 notas agudas, pausa, 2 notas agudas
      const notes = [
        { freq: 880, start: 0, duration: 0.15 },        // 2 notas agudas
        { freq: 880, start: 0.20, duration: 0.15 },
        // pausa 500ms
        { freq: 880, start: 0.85, duration: 0.15 },     // 2 notas agudas
        { freq: 880, start: 1.05, duration: 0.18 },
      ];
      
      notes.forEach(note => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination); // Som direto
        gainNode.connect(reverb); // Som com reverb
        
        oscillator.frequency.value = note.freq;
        oscillator.type = 'square';
        
        const startTime = audioContext.currentTime + note.start;
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + note.duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + note.duration);
      });
    } catch (e) {
      console.log('Audio não suportado:', e);
    }
  };

  // Função para tocar som de celebração "tadaaa"
  const playCelebrationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Criar notas do "tadaaa" (Dó-Mi-Sol em sequência rápida)
      const notes = [
        { freq: 523.25, start: 0, duration: 0.15 },      // Dó5
        { freq: 659.25, start: 0.12, duration: 0.15 },   // Mi5
        { freq: 783.99, start: 0.24, duration: 0.4 },    // Sol5 (mais longo)
      ];
      
      notes.forEach(note => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = note.freq;
        oscillator.type = 'sine';
        
        // Envelope de volume para som mais suave
        const startTime = audioContext.currentTime + note.start;
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(0.5, startTime + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + note.duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + note.duration);
      });
    } catch (e) {
      console.log('Audio não suportado:', e);
    }
  };

  // Função para disparar confetes - explosão única de 5 segundos
  const fireConfetti = () => {
    const count = 300;
    const defaults = {
      origin: { y: 0.6 },
      zIndex: 9999,
      ticks: 500, // Duração mais longa das partículas (5 segundos)
      gravity: 0.8,
      decay: 0.94,
      colors: ['#10b981', '#34d399', '#6ee7b7', '#fbbf24', '#f59e0b', '#3b82f6', '#8b5cf6']
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio),
      });
    }

    // Explosão única de múltiplas direções
    fire(0.25, { spread: 26, startVelocity: 55, origin: { x: 0.2 } });
    fire(0.2, { spread: 60, origin: { x: 0.35 } });
    fire(0.35, { spread: 100, scalar: 0.8, origin: { x: 0.5 } });
    fire(0.2, { spread: 60, origin: { x: 0.65 } });
    fire(0.25, { spread: 26, startVelocity: 55, origin: { x: 0.8 } });
  };

  // Carregar campanhas e restaurar banner do localStorage ao iniciar
  useEffect(() => {
    const loadBannerFromStorage = async () => {
      const savedBanner = localStorage.getItem('campaignCompletedBanner');
      if (savedBanner) {
        try {
          const data = JSON.parse(savedBanner);
          // Buscar dados atualizados do backend
          if (data.campaignId) {
            try {
              await refreshCampaignStatus(data.campaignId);
              const response = await getCampaignProgress(data.campaignId);
              const progress = response.data;
              
              // Verificar se a campanha ainda está ativa (running ou completed)
              if (progress.status === 'running' || progress.status === 'completed') {
                setCampaignProgress(progress);
                
                // Se o backend marcou como completed, confiar — campanha terminou
                // (mesmo que percentage < 100 por contatos deletados antes do envio).
                if (progress.status === 'completed') {
                  setCampaignCompleted(true);
                } else {
                  // Status === 'running' → iniciar polling
                  setCampaignCompleted(false);
                  setRunningCampaign({ _id: data.campaignId, name: progress.name });
                }
              } else {
                // Campanha foi cancelada ou está em outro status, limpar
                localStorage.removeItem('campaignCompletedBanner');
              }
            } catch (e) {
              // Se erro, remover do localStorage
              localStorage.removeItem('campaignCompletedBanner');
            }
          }
        } catch (e) {
          localStorage.removeItem('campaignCompletedBanner');
        }
      }
    };
    
    loadBannerFromStorage();
    fetchCampaigns();
    fetchGroups();
    fetchUsers();
    fetchLimits();
  }, []);

  // Verificar periodicamente se há campanhas iniciadas pelo scheduler
  useEffect(() => {
    const checkForRunningCampaigns = async () => {
      // Se já tem uma campanha em execução (não concluída), não verificar
      if (runningCampaign && !campaignCompleted) {
        console.log('[checkForRunningCampaigns] Já tem campanha em execução, ignorando');
        return;
      }
      
      try {
        const response = await getCampaigns({ size: 1000 });
        const allCampaigns = response.data.data;
        
        // Verificar se há alguma campanha rodando
        const runningCampaigns = allCampaigns.filter(c => c.status === 'running');
        console.log('[checkForRunningCampaigns] Campanhas rodando:', runningCampaigns.length);
        
        if (runningCampaigns.length > 0) {
          const campaign = runningCampaigns[0];
          console.log('[checkForRunningCampaigns] Campanha detectada:', campaign.name, campaign._id);
          
          // Verificar se há banner salvo no localStorage
          const savedBanner = localStorage.getItem('campaignCompletedBanner');
          console.log('[checkForRunningCampaigns] Banner salvo:', savedBanner);
          
          // Se há um banner salvo, verificar se é da mesma campanha
          if (savedBanner) {
            try {
              const data = JSON.parse(savedBanner);
              // Se é a mesma campanha, não fazer nada (já está sendo monitorada)
              if (data.campaignId === campaign._id) {
                console.log('[checkForRunningCampaigns] Mesma campanha, ignorando');
                return;
              }
            } catch (e) {}
          }
          
          // Se é uma campanha diferente da que estava rodando antes (ou se a anterior já terminou)
          if (!runningCampaign || runningCampaign._id !== campaign._id || campaignCompleted) {
            console.log('[checkForRunningCampaigns] Iniciando banner para campanha:', campaign.name);
            
            // Limpar estado de conclusão anterior se existir (mesmo comportamento de iniciar manualmente)
            if (campaignCompleted || savedBanner) {
              localStorage.removeItem('campaignCompletedBanner');
              setCampaignCompleted(false);
              setCampaignProgress(null);
            }
            
            // Salvar no localStorage para persistir (mesmo comportamento de iniciar manualmente)
            localStorage.setItem('campaignCompletedBanner', JSON.stringify({
              campaignId: campaign._id
            }));
            
            // Definir nova campanha em execução
            setRunningCampaign(campaign);
            setCampaigns(allCampaigns);
            toast.info(`Campanha "${campaign.name}" iniciada automaticamente`);
            
            // Buscar progresso imediatamente (não esperar o useEffect)
            try {
              console.log('[checkForRunningCampaigns] Buscando progresso...');
              await refreshCampaignStatus(campaign._id).catch(() => {});
              const progressResponse = await getCampaignProgress(campaign._id);
              console.log('[checkForRunningCampaigns] Progresso:', progressResponse.data);
              setCampaignProgress(progressResponse.data);
            } catch (e) {
              console.error('Erro ao buscar progresso inicial:', e);
            }
          } else {
            console.log('[checkForRunningCampaigns] Condição não atendida para iniciar banner');
          }
        }
      } catch (error) {
        console.error('Erro ao verificar campanhas em execução:', error);
      }
    };

    // Verificar a cada 3 segundos para detectar campanhas agendadas iniciando
    const interval = setInterval(checkForRunningCampaigns, 3000);
    
    // Verificar imediatamente também
    checkForRunningCampaigns();
    
    return () => clearInterval(interval);
  }, [runningCampaign, campaignCompleted]);
  
  const fetchLimits = async () => {
    try {
      const response = await getMessageLimits();
      setIsUnlimited(response.data.unlimited === true);
    } catch (error) {
      console.error('Error fetching limits:', error);
    }
  };

  // Polling para progresso da campanha
  useEffect(() => {
    if (runningCampaign && !campaignCompleted) {
      // Buscar progresso imediatamente
      fetchProgress(runningCampaign._id);
      
      // Configurar polling a cada 2 segundos
      progressIntervalRef.current = setInterval(() => {
        fetchProgress(runningCampaign._id);
      }, 2000);
    } else if (!campaignCompleted && !localStorage.getItem('campaignCompletedBanner')) {
      // Só limpar se não há banner salvo
      setCampaignProgress(null);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [runningCampaign, campaignCompleted]);

  const fetchProgress = async (campaignId) => {
    try {
      // Primeiro, atualizar os status das mensagens consultando a API WZAP
      await refreshCampaignStatus(campaignId).catch(() => {});
      
      // Depois, buscar o progresso atualizado
      const response = await getCampaignProgress(campaignId);
      const progress = response.data;
      setCampaignProgress(progress);
      
      // Se backend marcou como completed, confiar e fechar (mesmo se percentage<100)
      if (progress.status === 'completed' && !campaignCompleted) {
        setCampaignCompleted(true);
        fireConfetti();
        playCelebrationSound();
        
        localStorage.setItem('campaignCompletedBanner', JSON.stringify({
          campaignId: campaignId
        }));
        
        fetchCampaigns();
        
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error('Erro ao buscar progresso:', error);
    }
  };

  const handleCloseBanner = () => {
    // Remover do localStorage
    localStorage.removeItem('campaignCompletedBanner');
    
    // Parar polling se ainda estiver rodando
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    // Iniciar animação de fade out
    setBannerFading(true);
    
    // Após a animação terminar (1 segundo), limpar os estados
    setTimeout(() => {
      setCampaignCompleted(false);
      setRunningCampaign(null);
      setCampaignProgress(null);
      setBannerFading(false);
    }, 1000); // 1 segundo para a animação de fade
  };

  const fetchCampaigns = async () => {
    try {
      const response = await getCampaigns({ size: 1000 });
      setCampaigns(response.data.data);
      setListCache('campaigns', response.data.data); // grava/atualiza o cache SWR (chave única)

      // Verificar se há campanha em execução
      // Mas não sobrescrever se já temos uma campanha do localStorage (banner persistido)
      const savedBanner = localStorage.getItem('campaignCompletedBanner');
      if (!campaignCompleted && !savedBanner) {
        const hasRunning = response.data.data.find(c => c.status === 'running');
        setRunningCampaign(hasRunning || null);
      }
    } catch (error) {
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('token');
      const groupsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(res => res.json());
      setGroups(groupsRes);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/senders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAvailableUsers(data || []);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const handleOpenDialog = (campaign = null) => {
    if (campaign) {
      setEditingCampaign(campaign);
      setFormData({
        name: campaign.name,
        message_template: campaign.message_template,
      });
      // Carregar os grupos da campanha existente
      setSelectedGroups(campaign.group_ids || []);
      // Carregar anexo existente se houver
      if (campaign.attachment) {
        setSelectedFile({
          name: campaign.attachment.filename,
          size: campaign.attachment.size,
          type: campaign.attachment.content_type,
          category: campaign.attachment.category,
          existingAttachment: true, // Flag to indicate it's an existing attachment
        });
      } else {
        setSelectedFile(null);
      }
    } else {
      setEditingCampaign(null);
      setFormData({ name: '', message_template: '' });
      setSelectedGroups([]);
      setSelectedFile(null);
    }
    setViewingCampaign(null);
    setDialogOpen(true);
  };

  // Atalho vindo da tela Grupos (?group=<id>): abre "Nova Campanha" já com o grupo pré-selecionado.
  useEffect(() => {
    const g = searchParams.get('group');
    if (g) {
      handleOpenDialog();          // diálogo de criar, limpo
      setSelectedGroups([g]);      // sobrescreve o [] do handleOpenDialog → grupo já marcado
      setSearchParams({}, { replace: true }); // limpa o param pra não reabrir em refresh/voltar
    }
  }, []);

  const handleViewCampaign = (campaign) => {
    setViewingCampaign(campaign);
    setEditingCampaign(null);
    setFormData({
      name: campaign.name,
      message_template: campaign.message_template,
    });
    setSelectedGroups(campaign.group_ids || []);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setViewingCampaign(null);
    setEditingCampaign(null);
    setFormData({ name: '', message_template: '' });
    setSelectedGroups([]);
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      let campaignId;
      
      if (editingCampaign) {
        await updateCampaign(editingCampaign._id, {
          name: formData.name,
          message_template: formData.message_template,
          group_ids: selectedGroups,
        });
        campaignId = editingCampaign._id;
        
        // Handle attachment changes
        if (selectedFile && selectedFile.file && !selectedFile.existingAttachment) {
          // New file to upload
          await uploadCampaignAttachment(campaignId, selectedFile.file);
        } else if (!selectedFile && editingCampaign.attachment) {
          // Attachment was removed
          await deleteCampaignAttachment(campaignId);
        }
        
        toast.success('Campanha atualizada');
      } else {
        const response = await createCampaign({
          name: formData.name,
          message_template: formData.message_template,
          group_ids: selectedGroups,
        });
        campaignId = response.data._id;
        
        // Upload attachment if selected
        if (selectedFile && selectedFile.file) {
          await uploadCampaignAttachment(campaignId, selectedFile.file);
        }
        
        toast.success('Campanha criada');
      }
      
      setDialogOpen(false);
      setFormData({ name: '', message_template: '' });
      setSelectedGroups([]);
      setEditingCampaign(null);
      setSelectedFile(null);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar campanha');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAttachment = async (campaignId, attachment, campaign) => {
    if (campaign?.attachment_expired) {
      toast.error('Arquivo expirado (removido por purga)');
      return;
    }
    try {
      const response = await getCampaignAttachment(campaignId);
      const blob = new Blob([response.data], { type: attachment.content_type });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 410 || detail.includes('purga')) {
        toast.error('Arquivo expirado (removido por purga)');
      } else {
        toast.error('Erro ao abrir anexo');
      }
    }
  };

  const handleStart = (campaign) => {
    // Se há banner de conclusão aberto, fechar primeiro (sem animação)
    if (campaignCompleted) {
      localStorage.removeItem('campaignCompletedBanner');
      setCampaignCompleted(false);
      setRunningCampaign(null);
      setCampaignProgress(null);
    }
    
    // Verificar se já há campanha em execução (não concluída)
    if (runningCampaign && !campaignCompleted) {
      toast.error(`Já existe uma campanha em execução: "${runningCampaign.name}". Aguarde a conclusão.`);
      return;
    }
    setCampaignToStart(campaign);
    setStartDialogOpen(true);
    setWarningPulsing(true);
    playWarningSound();
    // Parar a animação após o som terminar (~1.3s)
    setTimeout(() => setWarningPulsing(false), 1300);
  };

  // Reenviar falhas: cria campanha NOVA (draft) só com os contatos que falharam e
  // abre o diálogo de confirmação para o usuário revisar e clicar "Iniciar".
  // Só é permitido UM reenvio por campanha (o backend bloqueia is_retry).
  const [retryingId, setRetryingId] = useState(null);
  // Id do reenvio recém-criado (draft) que deve ser DESCARTADO se o usuário cancelar
  // o diálogo de início — evita rascunho "Reenvio —" órfão na lista.
  const [pendingRetryId, setPendingRetryId] = useState(null);
  const handleRetryFailed = async (campaign) => {
    if (retryingId) return;
    setRetryingId(campaign._id);
    try {
      const res = await retryCampaignFailed(campaign._id);
      const newCampaign = res.data;
      // Fechar o modal de detalhe, se aberto
      if (viewingCampaign) handleCloseDialog();
      await fetchCampaigns();
      toast.success(`Reenvio criado com ${newCampaign.total_contacts} contato(s). Revise e clique em Iniciar.`);
      setPendingRetryId(newCampaign._id);
      handleStart(newCampaign);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar reenvio de falhas');
    } finally {
      setRetryingId(null);
    }
  };

  // Cancelar o diálogo de início. Se for um reenvio ainda não iniciado, apaga o rascunho.
  const cancelStart = () => {
    setStartDialogOpen(false);
    setCampaignToStart(null);
    if (pendingRetryId) {
      const idToDiscard = pendingRetryId;
      setPendingRetryId(null);
      deleteCampaign(idToDiscard)
        .then(() => fetchCampaigns())
        .catch(() => {});
    }
  };

  const confirmStart = async () => {
    if (!campaignToStart) return;

    // Início confirmado: o reenvio será disparado, então não descartar mais.
    setPendingRetryId(null);

    // Auto-fechar banner de conclusão de campanha anterior (equivalente a clicar "OK")
    // antes de iniciar a nova campanha, garantindo UI limpa para o novo progresso.
    if (campaignCompleted || campaignProgress) {
      setCampaignCompleted(false);
      setCampaignProgress(null);
      localStorage.removeItem('campaignCompletedBanner');
    }

    setStartingId(campaignToStart._id);
    setRunningCampaign(campaignToStart); // Marcar como em execução localmente
    setStartDialogOpen(false);
    setCampaignToStart(null);
    
    // Salvar no localStorage para persistir entre navegações
    localStorage.setItem('campaignCompletedBanner', JSON.stringify({
      campaignId: campaignToStart._id
    }));
    
    // Iniciar campanha sem aguardar conclusão
    // O polling vai acompanhar o progresso e detectar a conclusão
    startCampaign(campaignToStart._id)
      .catch((error) => {
        // Se der erro no início, limpar estado
        toast.error(error.response?.data?.detail || 'Erro ao iniciar campanha');
        setRunningCampaign(null);
        setCampaignProgress(null);
        setCampaignCompleted(false);
        setStartingId(null);
        localStorage.removeItem('campaignCompletedBanner');
        fetchCampaigns();
      });
    
    // Limpar startingId após um pequeno delay (para o UI mostrar "Enviando...")
    setTimeout(() => {
      setStartingId(null);
    }, 500);
  };

  // Funções de agendamento
  const handleOpenSchedule = (campaign) => {
    setCampaignToSchedule(campaign);
    setScheduleMode('once'); // sempre abre no modo "Uma vez"
    // Carregar estados de notificação apenas se a campanha já tiver agendamento E notificação ativa
    if (campaign.scheduled_at && campaign.notify_on_complete) {
      setNotifyByWhatsApp(true);
      setNotifyWhatsAppNumber(campaign.notify_whatsapp_number || '');
    } else {
      // Default: checkbox desmarcado para novos agendamentos
      setNotifyByWhatsApp(false);
      setNotifyWhatsAppNumber('');
    }
    // Se já tem agendamento, preencher o campo
    if (campaign.scheduled_at) {
      const date = new Date(campaign.scheduled_at);
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setScheduleDateTime(localDateTime);
    } else {
      // Default: agora + 5 minutos (em horário local, evita rolar para o dia seguinte
      // quando o usuário está próximo da meia-noite local).
      const now = new Date();
      now.setMinutes(now.getMinutes() + 5, 0, 0);
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setScheduleDateTime(localDateTime);
    }
    setScheduleDialogOpen(true);
  };

  // Função chamada ao clicar "Agendar" no modal de data/hora
  const handleScheduleClick = () => {
    if (!campaignToSchedule || !scheduleDateTime) return;
    
    const scheduledDate = new Date(scheduleDateTime);
    if (scheduledDate <= new Date()) {
      toast.error('A data de agendamento deve ser no futuro');
      return;
    }
    
    // Fechar modal de data/hora e abrir modal de confirmação
    setScheduleDialogOpen(false);
    setScheduleConfirmDialogOpen(true);
    setScheduleWarningPulsing(true);
    playWarningSound();
    setTimeout(() => setScheduleWarningPulsing(false), 1300);
  };

  // Confirmar agendamento após aviso
  const confirmSchedule = async () => {
    if (!campaignToSchedule || !scheduleDateTime) return;
    
    // Validar número de WhatsApp se notificação estiver ativada
    if (notifyByWhatsApp && !notifyWhatsAppNumber.trim()) {
      toast.error('Digite o número de WhatsApp para notificação');
      return;
    }
    
    setScheduling(true);
    try {
      const scheduledDate = new Date(scheduleDateTime);
      const updateData = { 
        scheduled_at: scheduledDate.toISOString(),
        notify_on_complete: notifyByWhatsApp,
        notify_whatsapp_number: notifyByWhatsApp ? notifyWhatsAppNumber.trim() : null
      };
      await updateCampaign(campaignToSchedule._id, updateData);
      toast.success('Campanha agendada com sucesso!');
      setScheduleConfirmDialogOpen(false);
      setCampaignToSchedule(null);
      setScheduleDateTime('');
      setNotifyByWhatsApp(false);
      setNotifyWhatsAppNumber('');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao agendar campanha');
    } finally {
      setScheduling(false);
    }
  };

  // Criar molde recorrente (modo "Recorrente" do Schedule Dialog)
  const handleCreateRecurring = async () => {
    if (!campaignToSchedule) return;
    const [hh, mm] = recurTime.split(':').map(Number);
    if (Number.isNaN(hh) || Number.isNaN(mm)) {
      toast.error('Informe um horário válido');
      return;
    }
    if (notifyByWhatsApp && !notifyWhatsAppNumber.trim()) {
      toast.error('Digite o número de WhatsApp para notificação');
      return;
    }
    const payload = {
      freq: recurFreq, hour: hh, minute: mm, end_mode: recurEndMode,
      notify_on_complete: notifyByWhatsApp,
      notify_whatsapp_number: notifyByWhatsApp ? notifyWhatsAppNumber.trim() : null,
    };
    if (recurFreq === 'weekly') payload.weekday = Number(recurWeekday);
    if (recurFreq === 'monthly') payload.day_of_month = Number(recurDayOfMonth);
    if (recurFreq === 'every_n_days') {
      const n = Number(recurIntervalDays);
      if (!n || n < 1) { toast.error('Informe de quantos em quantos dias'); return; }
      payload.interval_days = n;
    }
    if (recurEndMode === 'on_date') {
      if (!recurEndDate) { toast.error('Informe a data de término'); return; }
      payload.end_date = new Date(recurEndDate + 'T23:59:00').toISOString();
    }
    if (recurEndMode === 'count') {
      const n = Number(recurMaxRuns);
      if (!n || n < 1) { toast.error('Informe quantas vezes repetir'); return; }
      payload.max_runs = n;
    }
    const isEditing = campaignToSchedule.status === 'recurring' || campaignToSchedule.status === 'recurring_done';
    setCreatingRecurring(true);
    try {
      if (isEditing) {
        await updateCampaignRecurrence(campaignToSchedule._id, payload);
        toast.success('Recorrência atualizada!');
      } else {
        await makeCampaignRecurring(campaignToSchedule._id, payload);
        toast.success('Campanha recorrente criada!');
      }
      setScheduleDialogOpen(false);
      setCampaignToSchedule(null);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar recorrência');
    } finally {
      setCreatingRecurring(false);
    }
  };

  // Pausar/reativar um molde recorrente
  const [togglingRecurrence, setTogglingRecurrence] = useState(null);
  const handleToggleRecurrence = async (molde) => {
    if (togglingRecurrence) return;
    setTogglingRecurrence(molde._id);
    try {
      const res = await toggleCampaignRecurrence(molde._id);
      toast.success(res.data?.active ? 'Recorrência reativada' : 'Recorrência pausada');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar recorrência');
    } finally {
      setTogglingRecurrence(null);
    }
  };

  // Abrir o Schedule Dialog em modo Recorrente já preenchido para EDITAR o molde
  const handleEditRecurrence = (molde) => {
    const r = molde.recurrence || {};
    setCampaignToSchedule(molde);
    setScheduleMode('recurring');
    setRecurFreq(r.freq || 'daily');
    setRecurTime(`${String(r.hour ?? 9).padStart(2, '0')}:${String(r.minute ?? 0).padStart(2, '0')}`);
    setRecurWeekday(String(r.weekday ?? 0));
    setRecurDayOfMonth(String(r.day_of_month ?? 1));
    setRecurIntervalDays(String(r.interval_days ?? 3));
    setRecurEndMode(r.end_mode || 'never');
    setRecurEndDate(r.end_date ? new Date(r.end_date).toISOString().slice(0, 10) : '');
    setRecurMaxRuns(String(r.max_runs ?? 10));
    // Preferência de relatório por WhatsApp já gravada no molde
    setNotifyByWhatsApp(!!molde.notify_on_complete);
    setNotifyWhatsAppNumber(molde.notify_whatsapp_number || '');
    setScheduleDialogOpen(true);
  };

  // Fase D — abrir o modal "Ver envios" e carregar as instâncias já disparadas pelo molde
  const openInstances = async (molde) => {
    setInstancesMolde(molde);
    setInstances([]);
    setInstancesLoading(true);
    try {
      const res = await getCampaignInstances(molde._id);
      setInstances(res.data.instances || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar os envios');
    } finally {
      setInstancesLoading(false);
    }
  };

  // Cancelar confirmação de agendamento (voltar ao modal de data/hora)
  const cancelScheduleConfirm = () => {
    setScheduleConfirmDialogOpen(false);
    setScheduleDialogOpen(true);
  };

  const handleCancelSchedule = async (campaign) => {
    try {
      await cancelCampaignSchedule(campaign._id);
      toast.success('Agendamento cancelado');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar agendamento');
    }
  };

  // Molde recorrente: cancelar devolve a campanha para RASCUNHO em vez de excluí-la
  // (mesma ideia do handleCancelSchedule da campanha agendada). Os envios já feitos
  // pelo molde são preservados: são histórico.
  const handleCancelRecurrence = async (molde) => {
    try {
      await cancelCampaignRecurrence(molde._id);
      toast.success('Agendamento cancelado. A campanha voltou para os rascunhos.');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar agendamento');
    }
  };

  const handleDelete = (campaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!campaignToDelete) return;

    try {
      await deleteCampaign(campaignToDelete._id);
      toast.success('Campanha excluída');
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      fetchCampaigns();
    } catch (error) {
      toast.error('Erro ao excluir campanha');
    }
  };

  const handleEmojiSelect = (emoji) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentMessage = formData.message_template;
      const newMessage = currentMessage.substring(0, start) + emoji + currentMessage.substring(end);
      setFormData({ ...formData, message_template: newMessage });
      // Set cursor position after emoji
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    } else {
      setFormData({ ...formData, message_template: formData.message_template + emoji });
    }
  };

  // Insere o marcador do nome no cursor — o usuário não precisa digitar {{name}}.
  const handleInsertName = () => {
    const token = '{{name}}';
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const current = formData.message_template;
      const newMessage = current.substring(0, start) + token + current.substring(end);
      setFormData({ ...formData, message_template: newMessage });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + token.length, start + token.length);
      }, 0);
    } else {
      setFormData({ ...formData, message_template: formData.message_template + token });
    }
  };

  // Rótulo da frequência da recorrência (usado no badge do molde).
  const recurrenceLabel = (rec) => {
    if (!rec) return 'Recorrente';
    switch (rec.freq) {
      case 'daily': return 'Diária';
      case 'weekly': return 'Semanal';
      case 'monthly': return 'Mensal';
      case 'every_n_days': return `A cada ${rec.interval_days || 'X'} dias`;
      default: return 'Recorrente';
    }
  };

  const getStatusBadge = (status, campaign) => {
    const styles = {
      draft: 'bg-slate-200 text-slate-800',
      scheduled: 'bg-blue-100 text-blue-800',
      running: 'bg-amber-100 text-amber-800',
      completed: 'bg-teal-100 text-teal-800',
      failed: 'bg-red-100 text-red-800',
      recurring: 'bg-indigo-100 text-indigo-800',
      recurring_done: 'bg-slate-200 text-slate-600',
    };
    const labels = {
      draft: 'Rascunho',
      scheduled: 'Agendada',
      running: 'Enviando',
      completed: 'Concluída',
      failed: 'Falhou',
      recurring: `🔁 ${recurrenceLabel(campaign?.recurrence)}`,
      recurring_done: `🔁 ${recurrenceLabel(campaign?.recurrence)} (encerrada)`,
    };
    return (
      <Badge className={styles[status] || styles.draft}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Filtrar campanhas
  // Moldes recorrentes: NÃO entram na lista de campanhas — vivem no painel próprio no topo.
  // (Ao terminar, o molde é removido pelo backend; recurring_done é estado legado.)
  const recurringMoldes = campaigns.filter((c) => c.status === 'recurring');

  const filteredCampaigns = campaigns.filter((campaign) => {
    // Moldes recorrentes ficam fora da tabela (têm painel próprio)
    if (campaign.status === 'recurring' || campaign.status === 'recurring_done') {
      return false;
    }
    // Filtro por nome
    if (nameFilter && !campaign.name.toLowerCase().includes(nameFilter.toLowerCase())) {
      return false;
    }
    // Filtro por status
    if (statusFilter && campaign.status !== statusFilter) {
      return false;
    }
    // Filtro por enviado por
    if (sentByFilter && campaign.started_by !== sentByFilter) {
      return false;
    }
    // Filtro por data inicial
    if (dateFromFilter) {
      const campaignDate = new Date(campaign.created_at).setHours(0, 0, 0, 0);
      const filterDate = new Date(dateFromFilter).setHours(0, 0, 0, 0);
      if (campaignDate < filterDate) return false;
    }
    // Filtro por data final
    if (dateToFilter) {
      const campaignDate = new Date(campaign.created_at).setHours(0, 0, 0, 0);
      const filterDate = new Date(dateToFilter).setHours(0, 0, 0, 0);
      if (campaignDate > filterDate) return false;
    }
    return true;
  });

  const handleClearFilters = () => {
    setNameFilter('');
    setStatusFilter('');
    setSentByFilter('');
    setDateFromFilter('');
    setDateToFilter('');
  };

  // Check if user can edit campaigns (ADMIN or USER_EDITOR)
  // Already provided by canEdit from useAuth

  return (
    <div className="flex flex-col h-full">
      {/* Painel de progresso da campanha em execução */}
      {(runningCampaign || campaignCompleted) && (
        <div 
          className={`rounded-lg p-3 mb-4 transition-all duration-1000 ${
            bannerFading ? 'opacity-0 transform -translate-y-2' : 'opacity-100'
          } ${
            campaignCompleted 
              ? 'bg-gradient-to-r from-teal-50 to-teal-50 border border-teal-400' 
              : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {campaignCompleted ? (
                <>
                  <span className="font-semibold text-base text-teal-800">
                    <span className="text-lg">🎉</span> A campanha 
                    <Badge variant="outline" className="text-base py-0.5 mx-1 bg-teal-100 text-teal-800 border-teal-400">
                      {runningCampaign?.name || campaignProgress?.name}
                    </Badge>
                    foi concluída!
                  </span>
                </>
              ) : campaignProgress?.status === 'completed' && campaignProgress?.percentage < 100 ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                  <span className="font-semibold text-base text-blue-800">
                    Processando a campanha <strong>{runningCampaign?.name || campaignProgress?.name}</strong>
                  </span>
                </>
              ) : (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                  <span className="font-semibold text-base text-amber-800">
                    Enviando a campanha <strong>{runningCampaign?.name || campaignProgress?.name}</strong>
                  </span>
                </>
              )}
            </div>
            {!campaignCompleted && (
              <span className="text-sm text-amber-700 font-bold"><span className="text-lg">⚠️</span> Atenção! Apenas uma campanha por vez pode ser enviada.</span>
            )}
          </div>
          
          {campaignProgress && (
            <>
              {/* Barra de progresso */}
              <Progress 
                value={campaignProgress.percentage} 
                className={`h-2.5 mb-2 ${campaignCompleted ? '[&>div]:bg-teal-500' : ''}`} 
              />
              
              {/* Estatísticas em linha */}
              <div className="flex items-center justify-between text-base">
                <div className="flex items-center gap-4">
                  <span className={`font-bold ${campaignCompleted ? 'text-teal-700' : 'text-gray-800'}`}>
                    {campaignProgress.percentage}%
                  </span>
                  <span className={campaignCompleted ? 'text-teal-600' : 'text-blue-600'}>
                    {campaignProgress.processed}/{campaignProgress.total} finalizadas
                  </span>
                  <span className="flex items-center gap-1 text-teal-600">
                    <CheckCircle2 className="w-4 h-4" /> {campaignProgress.sent} enviadas
                  </span>
                  {campaignProgress.failed > 0 && (
                    <span className="flex items-center gap-1 text-red-600">
                      <XCircle className="w-4 h-4" /> {campaignProgress.failed} falhas
                    </span>
                  )}
                </div>
                {campaignCompleted ? (
                  <Button 
                    size="sm" 
                    onClick={handleCloseBanner}
                    className="bg-teal-600 hover:bg-teal-700 text-white h-8 px-5 text-sm"
                  >
                    OK
                  </Button>
                ) : (
                  <span className="text-amber-700 text-sm">
                    Faltam <strong>{campaignProgress.remaining}</strong> para concluir
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="sticky top-0 z-20 bg-background pb-4">
        <div className="flex items-center gap-3">
          {/* Name Filter */}
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Nome da campanha..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-9"
              data-testid="campaigns-name-filter"
            />
          </div>

          {/* Status Filter */}
          <div className="flex-1">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="campaigns-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Rascunho</SelectItem>
                <SelectItem value="scheduled">Agendada</SelectItem>
                <SelectItem value="running">Enviando</SelectItem>
                <SelectItem value="completed">Concluída</SelectItem>
                <SelectItem value="failed">Falhou</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sent By Filter */}
          <div className="flex-1">
            <Select value={sentByFilter} onValueChange={setSentByFilter}>
              <SelectTrigger data-testid="campaigns-sent-by-filter">
                <SelectValue placeholder="Enviado por" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((user) => (
                  <SelectItem key={user._id} value={user._id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date From Filter */}
          <div className="flex-1 flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">De</span>
            <Input
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              title="Data inicial"
              data-testid="campaigns-date-from-filter"
              className="dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:[color-scheme:dark]"
            />
          </div>

          {/* Date To Filter */}
          <div className="flex-1 flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Até</span>
            <Input
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              title="Data final"
              data-testid="campaigns-date-to-filter"
              className="dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:[color-scheme:dark]"
            />
          </div>

          {/* Clear Button */}
          <Button 
            variant="secondary" 
            onClick={handleClearFilters}
            className="bg-gray-200 dark:bg-gray-700 hover:bg-primary hover:text-primary-foreground"
          >
            <X className="w-4 h-4 mr-1" />
            Limpar
          </Button>

          {/* New Campaign Button */}
          {canEdit && (
            <Button onClick={() => handleOpenDialog()} data-testid="campaigns-create-button">
              <Plus className="w-4 h-4 mr-2" />
              Nova Campanha
            </Button>
          )}
        </div>
      </div>

      {/* Painel de agendamentos recorrentes — só aparece quando há molde */}
      {recurringMoldes.length > 0 && (
          <Card className="mb-4 shrink-0 border-indigo-200 dark:border-indigo-900/50">
            <button
              type="button"
              onClick={() => setRecurringOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <span className="flex items-center gap-2 font-semibold text-foreground">
                <Repeat className="w-5 h-5" />
                Agendamentos recorrentes
                <Badge variant="outline" className="border-transparent shadow bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
                  {recurringMoldes.length}
                </Badge>
              </span>
              {recurringOpen
                ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {recurringOpen && (
              <div className="px-4 pb-4 flex gap-2">
                <div
                  ref={recurringScrollRef}
                  className="flex-1 max-h-[118px] overflow-y-hidden grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 pr-1"
                >
                  {recurringMoldes.map((molde) => {
                    const paused = molde.recurrence?.active === false;
                    return (
                      <div key={molde._id} className="rounded-lg border bg-card p-2 flex flex-col gap-1">
                        <span className="font-medium text-sm leading-tight truncate" title={molde.name}>{molde.name}</span>
                        <Badge variant="outline" className="w-fit gap-1 border-transparent shadow bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300">
                          🔁 {recurrenceLabel(molde.recurrence)}
                        </Badge>
                        {paused ? (
                          <span className="text-[11px] text-amber-600 flex items-center gap-1 font-semibold"
                            title={molde.paused_reason === 'last_instance_failed' ? (molde.last_instance_error || 'A última execução falhou') : ''}>
                            <AlertCircle className="w-3 h-3 shrink-0" />
                            {molde.paused_reason === 'last_instance_failed' ? 'Pausada: última execução falhou' : 'Pausada'}
                          </span>
                        ) : molde.next_run_at ? (
                          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(molde.next_run_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        ) : <span className="text-[11px] text-muted-foreground">&nbsp;</span>}
                        <div className="flex gap-0.5 mt-0.5">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Ver envios" onClick={() => openInstances(molde)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7"
                            title={paused ? 'Reativar' : 'Pausar'}
                            onClick={() => handleToggleRecurrence(molde)}
                            disabled={togglingRecurrence === molde._id}
                          >
                            {togglingRecurrence === molde._id ? <Loader2 className="w-4 h-4 animate-spin" /> : paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Editar agendamento" onClick={() => handleEditRecurrence(molde)}>
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Editar campanha" onClick={() => handleOpenDialog(molde)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Cancelar agendamento (volta para rascunhos)" onClick={() => handleCancelRecurrence(molde)}>
                            <CircleX className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Setas pra andar as linhas de agendamento */}
                <div className="flex flex-col justify-center gap-1 shrink-0">
                  <Button variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => recurringScrollRef.current?.scrollBy({ top: -118, behavior: 'smooth' })}>
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => recurringScrollRef.current?.scrollBy({ top: 118, behavior: 'smooth' })}>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
      )}

      {/* Loading State - Centralizado */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-6 p-8">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-primary" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {Math.round(loadingPercent)}%
                </span>
              </div>
            </div>
            <p className={`text-muted-foreground text-center ${loadingSeconds >= 120 ? 'max-w-md' : ''}`}>
              {getLoadingMessage()}
            </p>
            {loadingSeconds >= 60 && (
              <p className="text-xs text-muted-foreground">
                {Math.floor(loadingSeconds / 60)}:{(loadingSeconds % 60).toString().padStart(2, '0')} decorridos
              </p>
            )}
          </div>
        </div>
      ) : (
      <>
      {/* Table */}
      <Card className="flex-1 flex flex-col min-h-0 mb-4">
        <CardContent className="p-0 flex-1 flex flex-col min-h-0">
          <Table data-testid="campaigns-table">
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Contatos</TableHead>
                <TableHead>Enviadas</TableHead>
                <TableHead>Falhas</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead>Enviado por</TableHead>
                <TableHead>Atualizada em</TableHead>
                <TableHead className="w-[120px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCampaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="empty-state">
                      <Megaphone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        {campaigns.length === 0 ? 'Nenhuma campanha criada ainda' : 'Nenhuma campanha encontrada com os filtros aplicados'}
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCampaigns.map((campaign) => (
                  <TableRow key={campaign._id} className="hover:bg-primary/5">
                    <TableCell className="font-medium py-2">{campaign.name}</TableCell>
                    <TableCell className="py-2">{campaign.total_contacts}</TableCell>
                    <TableCell className="text-teal-600 py-2">{campaign.sent_count}</TableCell>
                    <TableCell className="text-red-600 py-2">{campaign.failed_count}</TableCell>
                    <TableCell className="py-2">
                      <div className="flex flex-col items-center">
                        {getStatusBadge(campaign.status, campaign)}
                        {campaign.status === 'scheduled' && campaign.scheduled_at && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1 font-semibold">
                            <Clock className="w-3 h-3" />
                            {new Date(campaign.scheduled_at).toLocaleString('pt-BR', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        )}
                        {campaign.status === 'failed' && campaign.error && (
                          <span className="text-[11px] text-red-600 text-center mt-1 max-w-[180px] leading-tight" title={campaign.error}>
                            {campaign.error}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2">
                      {campaign.started_by_name || campaign.created_by_name || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground py-2">
                      {new Date(campaign.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex gap-1">
                        {canEdit && campaign.status === 'draft' && startingId !== campaign._id && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenDialog(campaign)}
                              className="h-8 w-8"
                              title="Editar campanha"
                              disabled={!!runningCampaign}
                            >
                              <Edit className={runningCampaign ? 'text-gray-400' : ''} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStart(campaign)}
                              className="h-8 w-8"
                              title={runningCampaign ? `Aguarde a campanha "${runningCampaign.name}" terminar` : "Iniciar campanha"}
                              disabled={!!runningCampaign}
                            >
                              <Play className={runningCampaign ? 'text-gray-400' : ''} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenSchedule(campaign)}
                              className="h-8 w-8"
                              title="Agendar envio"
                              disabled={!!runningCampaign}
                            >
                              <Calendar className={runningCampaign ? 'text-gray-400' : ''} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(campaign)}
                              className="h-8 w-8"
                              title="Excluir campanha"
                              disabled={!!runningCampaign}
                            >
                              <Trash2 className={runningCampaign ? 'text-gray-400' : ''} />
                            </Button>
                          </>
                        )}
                        {canEdit && campaign.status === 'scheduled' && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenSchedule(campaign)}
                              className="h-8 w-8"
                              title="Editar agendamento"
                              disabled={!!runningCampaign}
                            >
                              <Edit className={runningCampaign ? 'text-gray-400' : ''} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleCancelSchedule(campaign)}
                              className="h-8 w-8"
                              title="Cancelar agendamento"
                              disabled={!!runningCampaign}
                            >
                              {/* CircleX (não a lixeira): cancelar o agendamento só tira
                                  a data, a campanha continua existindo como rascunho */}
                              <CircleX className={runningCampaign ? 'text-gray-400' : ''} />
                            </Button>
                          </>
                        )}
                        {campaign.status === 'completed' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleViewCampaign(campaign)}
                            className="h-8 w-8"
                            title="Visualizar campanha"
                          >
                            <Eye />
                          </Button>
                        )}
                        {campaign.status === 'completed' && campaign.failed_count > 0 && !campaign.is_retry && !campaign.has_retry && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRetryFailed(campaign)}
                            className="h-7 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-950/30"
                            title="Reenviar as mensagens que falharam"
                            disabled={!!retryingId || !!runningCampaign}
                          >
                            {retryingId === campaign._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                            Reenviar falhas
                          </Button>
                        )}
                        {startingId === campaign._id && (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Enviando...</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </>
      )}

      {/* Create/Edit/View Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {viewingCampaign 
                ? 'Visualizar Campanha Enviada' 
                : editingCampaign 
                  ? 'Editar Campanha' 
                  : 'Nova Campanha'}
            </DialogTitle>
          </DialogHeader>
          
          {viewingCampaign ? (
            // Modo de visualização (read-only)
            <div className="space-y-4 py-4">
              {/* Info da campanha */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="flex items-center justify-between text-blue-700 text-sm">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    <span>Campanha finalizada em {new Date(viewingCampaign.finalized_at || viewingCampaign.completed_at || viewingCampaign.created_at).toLocaleDateString('pt-BR')} às {new Date(viewingCampaign.finalized_at || viewingCampaign.completed_at || viewingCampaign.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-muted-foreground">Duração: </span>
                    <span className="font-medium">
                      {(() => {
                        const start = new Date(viewingCampaign.started_at || viewingCampaign.created_at);
                        const end = new Date(viewingCampaign.finalized_at || viewingCampaign.completed_at || viewingCampaign.created_at);
                        const diffMs = end - start;
                        const diffSecs = Math.floor(diffMs / 1000);
                        const hours = Math.floor(diffSecs / 3600);
                        const mins = Math.floor((diffSecs % 3600) / 60);
                        const secs = diffSecs % 60;
                        if (hours > 0) {
                          return `${String(hours).padStart(2, '0')} hora${hours > 1 ? 's' : ''} e ${String(mins).padStart(2, '0')} minuto${mins !== 1 ? 's' : ''}`;
                        }
                        if (mins > 0) {
                          return `${mins} minuto${mins !== 1 ? 's' : ''}`;
                        }
                        return `${secs} segundo${secs !== 1 ? 's' : ''}`;
                      })()}
                    </span>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Contatos:</span>
                    <span className="ml-1 font-medium">{viewingCampaign.total_contacts}</span>
                  </div>
                  {(() => {
                    const totalProcessed = (viewingCampaign.sent_count || 0) + (viewingCampaign.failed_count || 0);
                    const allFinalized = totalProcessed >= viewingCampaign.total_contacts;
                    
                    if (allFinalized) {
                      return (
                        <>
                          <div>
                            <span className="text-muted-foreground">Enviadas:</span>
                            <span className="ml-1 font-medium text-teal-600">{viewingCampaign.sent_count}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Falhas:</span>
                            <span className="ml-1 font-medium text-red-600">{viewingCampaign.failed_count}</span>
                          </div>
                        </>
                      );
                    } else {
                      return (
                        <div className="col-span-2">
                          <span className="text-amber-600 font-medium">⏳ Processando campanha...</span>
                        </div>
                      );
                    }
                  })()}
                </div>
                <div className="mt-2 text-sm">
                  <span className="text-muted-foreground">Enviado por:</span>
                  <span className="ml-1 font-medium">{viewingCampaign.started_by_name || '—'}</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Nome da Campanha</Label>
                <Input
                  value={formData.name}
                  disabled
                  className="bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Mensagem Enviada</Label>
                <Textarea
                  value={formData.message_template}
                  disabled
                  rows={4}
                  className="bg-gray-100 text-gray-600 cursor-not-allowed resize-none"
                />
              </div>

              {/* Grupos selecionados (read-only) */}
              {selectedGroups.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Grupos Selecionados</Label>
                  <div className="border rounded-md p-3 bg-gray-50">
                    <div className="flex flex-wrap gap-2">
                      {selectedGroups.map((groupId) => {
                        const group = groups.find(g => g._id === groupId);
                        return group ? (
                          <Badge key={groupId} variant="secondary" className="bg-gray-200 text-gray-600">
                            {group.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Anexo enviado (read-only) - clicável para abrir */}
              {viewingCampaign.attachment && (
                <div className="space-y-2 overflow-hidden">
                  <Label className="text-muted-foreground">Anexo Enviado</Label>
                  {viewingCampaign.attachment_expired ? (
                    // Anexo expirado - não clicável
                    <div className="w-full text-left border border-amber-200 rounded-md p-3 bg-amber-50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                        </div>
                        <div className="flex-1 overflow-hidden" style={{ minWidth: 0, width: 0 }}>
                          <p className="text-sm font-medium text-amber-700 truncate" title={viewingCampaign.attachment.filename}>
                            {viewingCampaign.attachment.filename}
                          </p>
                          <p className="text-xs text-amber-600">
                            Arquivo expirado (removido por purga)
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Anexo disponível - clicável
                    <button
                      type="button"
                      onClick={() => handleOpenAttachment(viewingCampaign._id, viewingCampaign.attachment)}
                      className="w-full text-left border rounded-md p-3 bg-gray-50 hover:bg-gray-100 hover:border-primary/50 transition-colors cursor-pointer overflow-hidden"
                      style={{ maxWidth: '100%' }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                          {viewingCampaign.attachment.category === 'image' && (
                            <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                          {viewingCampaign.attachment.category === 'video' && (
                            <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          )}
                          {viewingCampaign.attachment.category === 'audio' && (
                            <svg className="w-5 h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                          )}
                          {viewingCampaign.attachment.category === 'document' && (
                            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          )}
                        </div>
                        <div className="flex-1 overflow-hidden" style={{ minWidth: 0, width: 0 }}>
                          <p className="text-sm font-medium text-gray-700 truncate" title={viewingCampaign.attachment.filename}>
                            {viewingCampaign.attachment.filename}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {(viewingCampaign.attachment.size / 1024).toFixed(1)} KB • {viewingCampaign.attachment.category}
                          </p>
                        </div>
                        <div className="flex-shrink-0 text-gray-400">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </div>
                      </div>
                    </button>
                  )}
                  {!viewingCampaign.attachment_expired && (
                    <p className="text-xs text-muted-foreground">Clique para abrir o anexo</p>
                  )}
                </div>
              )}

              {/* Reenviar falhas — só em campanha concluída com falhas, que não é reenvio e ainda não foi reenviada */}
              {viewingCampaign.failed_count > 0 && !viewingCampaign.is_retry && !viewingCampaign.has_retry && (
                <div className="pt-2 border-t">
                  <Button
                    type="button"
                    onClick={() => handleRetryFailed(viewingCampaign)}
                    disabled={!!retryingId || !!runningCampaign}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white dark:bg-amber-600 dark:hover:bg-amber-700"
                  >
                    {retryingId === viewingCampaign._id
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <RotateCcw className="w-4 h-4 mr-2" />}
                    Reenviar {viewingCampaign.failed_count} falha(s)
                  </Button>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Cria uma nova campanha só com os contatos que falharam, para você revisar e iniciar.
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Modo de criação/edição
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome da Campanha</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Promoção de Verão"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="message">Mensagem</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleInsertName}
                        className="h-9 px-3 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/50 hover:text-teal-800 dark:hover:text-teal-200 flex items-center gap-2"
                        title="Insere o nome de cada contato automaticamente na mensagem"
                        data-testid="campaign-insert-name"
                      >
                        <span className="text-lg">👤</span>
                        <span className="text-sm">Inserir nome</span>
                      </Button>
                      {/* abre para baixo: no modal sobra espaço abaixo do botão */}
                      <EmojiPicker direction="down" onEmojiSelect={handleEmojiSelect} />
                    </div>
                  </div>
                  <Textarea
                    id="message"
                    ref={textareaRef}
                    value={formData.message_template}
                    onChange={(e) => setFormData({ ...formData, message_template: e.target.value })}
                    placeholder="Olá {{name}}, temos uma oferta especial para você!"
                    rows={4}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Clique em <strong>Inserir nome</strong> para personalizar a mensagem com o nome de cada pessoa.
                  </p>
                  {/* Altura reservada p/ o modal não pular quando a prévia aparece ao inserir o nome */}
                  <div className="min-h-[2.75rem]">
                    {formData.message_template.includes('{{name}}') && (
                      <div className="rounded-md bg-secondary/50 px-3 py-2 text-xs" data-testid="campaign-message-preview">
                        <span className="text-muted-foreground">Prévia (ex.: "João"): </span>
                        <span className="whitespace-pre-wrap">
                          {formData.message_template.split(/\{\{name\}\}/g).map((part, i, arr) => (
                            <React.Fragment key={i}>
                              {part}
                              {i < arr.length - 1 && <strong>João</strong>}
                            </React.Fragment>
                          ))}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* File Attachment */}
                <div className="space-y-2">
                  <Label>Anexo (opcional)</Label>
                  <FileAttachment
                    onFileSelect={setSelectedFile}
                    selectedFile={selectedFile}
                    onRemove={() => setSelectedFile(null)}
                    disabled={submitting}
                    isUnlimited={isUnlimited}
                  />
                </div>

                {/* Groups selection - available for both creating and editing */}
                {groups.length > 0 && (
                  <div className="space-y-2">
                    <Label>Selecionar Grupos</Label>
                    <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                      {groups.map((group) => (
                        <div key={group._id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`campaign-group-${group._id}`}
                            checked={selectedGroups.includes(group._id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedGroups([...selectedGroups, group._id]);
                              } else {
                                setSelectedGroups(selectedGroups.filter(id => id !== group._id));
                              }
                            }}
                          />
                          <Label htmlFor={`campaign-group-${group._id}`} className="cursor-pointer font-normal">
                            {group.name} ({group.contact_count} contatos)
                          </Label>
                        </div>
                      ))}
                    </div>
                    {editingCampaign && (
                      <p className="text-xs text-muted-foreground">
                        Atualmente: {editingCampaign.total_contacts} contatos. Alterar os grupos irá atualizar os contatos da campanha.
                      </p>
                    )}
                  </div>
                )}

                {/* Info about selection */}
                {!editingCampaign && (
                  <p className="text-xs text-muted-foreground">
                    {selectedGroups.length > 0
                      ? `Enviando para ${selectedGroups.length} grupo(s) selecionado(s)`
                      : 'Selecione pelo menos um grupo'}
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={submitting || (!editingCampaign && selectedGroups.length === 0)}>
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingCampaign ? 'Salvar Alterações' : 'Criar Campanha'}
                </Button>
              </DialogFooter>
            </form>
          )}
          
          {viewingCampaign && (
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Fechar
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Start Campaign Confirmation Dialog */}
      <Dialog open={startDialogOpen} onOpenChange={(open) => { if (open) { setStartDialogOpen(true); } else { cancelStart(); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl">Iniciar Campanha</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-base text-muted-foreground">
              Tem certeza que deseja iniciar a campanha <strong>{campaignToStart?.name}</strong>?
            </p>
            <p className="text-base text-muted-foreground">
              Isso enviará mensagens para <strong>{campaignToStart?.total_contacts} contatos</strong>.
            </p>

            {/* Advertência */}
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <div className="text-center mb-3">
                <span 
                  className={`text-3xl inline-block ${warningPulsing ? 'animate-pulse' : ''}`}
                  style={warningPulsing ? { 
                    animation: 'warningPulse 0.2s ease-in-out infinite',
                  } : {}}
                >⚠️</span>
                <span 
                  className="font-bold text-amber-800 ml-2 inline-block"
                  style={{ 
                    fontSize: '28px',
                    verticalAlign: '-2px',
                  }}
                >ATENÇÃO!</span>
              </div>
              <p className="text-base text-amber-700 text-center">
                Uma vez iniciada, a campanha <strong>NÃO poderá ser interrompida</strong> <strong>devido à natureza do próprio aplicativo WhatsApp</strong>. 
                Todas as mensagens serão enviadas automaticamente.
              </p>
            </div>
            <style>{`
              @keyframes warningPulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.3); }
              }
            `}</style>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelStart}>
              Cancelar
            </Button>
            <Button onClick={confirmStart} className="bg-amber-600 hover:bg-amber-700">
              Confirmar e Iniciar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl">Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-base text-muted-foreground">
              Tem certeza que deseja excluir {(campaignToDelete?.status === 'recurring' || campaignToDelete?.status === 'recurring_done') ? 'o agendamento' : 'a campanha'} <strong>{campaignToDelete?.name}</strong>?
            </p>
            {(campaignToDelete?.status === 'recurring' || campaignToDelete?.status === 'recurring_done') && (
              <p className="text-sm text-muted-foreground mt-2">
                A recorrência para de disparar. As campanhas já enviadas continuam no histórico.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={(open) => {
        setScheduleDialogOpen(open);
        if (!open) {
          // Resetar estados ao fechar o modal
          setNotifyByWhatsApp(false);
          setNotifyWhatsAppNumber('');
        }
      }}>
        <DialogContent className="max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Calendar className="w-5 h-5 text-foreground" />
              Agendar Campanha
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4 h-[620px] flex flex-col">
            <p className="text-sm text-muted-foreground">
              Agende o envio automático da campanha <strong>{campaignToSchedule?.name}</strong>.
            </p>

            {/* Toggle Uma vez / Recorrente — só para rascunho (recorrência exige draft) */}
            {campaignToSchedule?.status === 'draft' && (
            <div className="grid grid-cols-2 gap-2 p-1 bg-secondary/50 rounded-lg">
              <button
                type="button"
                onClick={() => setScheduleMode('once')}
                className={`text-sm font-medium py-2 rounded-md transition ${scheduleMode === 'once' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Uma vez
              </button>
              <button
                type="button"
                onClick={() => setScheduleMode('recurring')}
                className={`text-sm font-medium py-2 rounded-md transition flex items-center justify-center gap-1.5 ${scheduleMode === 'recurring' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Repeat className="w-3.5 h-3.5" /> Recorrente
              </button>
            </div>
            )}

            {scheduleMode === 'once' && (
            <div className="flex-1 flex flex-col gap-4">
            <div className="flex-1 flex flex-col">
              <ScheduleDateTimePicker
                value={scheduleDateTime}
                onChange={setScheduleDateTime}
                footer={scheduleDateTime && (() => {
                const sched = new Date(scheduleDateTime);
                if (Number.isNaN(sched.getTime())) return null;
                const now = new Date();
                const diffMinutes = Math.round((sched.getTime() - now.getTime()) / 60000);
                const isPast = diffMinutes < 0;
                const formatted = sched.toLocaleString('pt-BR', {
                  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
                  hour: '2-digit', minute: '2-digit',
                });
                const human = isPast
                  ? 'no passado (não será disparada)'
                  : diffMinutes < 60
                    ? `em ${diffMinutes} ${diffMinutes === 1 ? 'minuto' : 'minutos'}`
                    : diffMinutes < 1440
                      ? `em ${Math.round(diffMinutes / 60)} hora(s)`
                      : `em ${Math.round(diffMinutes / 1440)} dia(s)`;
                const cap = formatted.charAt(0).toUpperCase() + formatted.slice(1);
                return (
                  <div
                    data-testid="schedule-preview"
                    className={`rounded-lg border px-3 py-2 text-center ${isPast
                      ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900/40'
                      : 'bg-primary/10 border-primary/20'}`}
                  >
                    <p className={`text-sm font-semibold ${isPast ? 'text-red-600' : 'text-foreground'}`}>
                      {cap}
                    </p>
                    <p className={`text-xs mt-0.5 ${isPast ? 'text-red-600' : 'text-primary'}`}>
                      {isPast ? human : `Será disparada ${human}`}
                    </p>
                  </div>
                );
              })()}
              />
            </div>

            {/* grupo inferior: notificação + aviso */}
            <div className="space-y-3">
            {/* Notificação por WhatsApp */}
            <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="notifyByWhatsApp"
                  checked={notifyByWhatsApp}
                  onCheckedChange={(checked) => {
                    if (checked && (!user?.whatsapp || user.whatsapp.length < 10)) {
                      toast.error('Seu usuário não possui número de WhatsApp válido. Preencha em seu perfil.');
                      return;
                    }
                    setNotifyByWhatsApp(checked);
                    if (checked && user?.whatsapp) {
                      setNotifyWhatsAppNumber(user.whatsapp);
                    }
                  }}
                />
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-teal-600" />
                  <Label htmlFor="notifyByWhatsApp" className="text-sm font-normal cursor-pointer">
                    Receber notificação via WhatsApp após o término
                  </Label>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              A campanha será enviada automaticamente na data e hora selecionadas.
            </p>
            </div>
            </div>
            )}

            {scheduleMode === 'recurring' && (
            <div className="flex-1 flex flex-col gap-3">
              {/* Card: frequência + quando — preenche metade da altura */}
              <div className="flex-1 rounded-xl border bg-card p-4 flex flex-col justify-start gap-4">
                <div className="space-y-2">
                  <Label>Frequência</Label>
                  <Select value={recurFreq} onValueChange={setRecurFreq}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Diária</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="monthly">Mensal</SelectItem>
                      <SelectItem value="every_n_days">A cada X dias</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    {recurFreq === 'weekly' && (
                      <div className="space-y-2">
                        <Label>Dia da semana</Label>
                        <Select value={recurWeekday} onValueChange={setRecurWeekday}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Segunda</SelectItem>
                            <SelectItem value="1">Terça</SelectItem>
                            <SelectItem value="2">Quarta</SelectItem>
                            <SelectItem value="3">Quinta</SelectItem>
                            <SelectItem value="4">Sexta</SelectItem>
                            <SelectItem value="5">Sábado</SelectItem>
                            <SelectItem value="6">Domingo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {recurFreq === 'monthly' && (
                      <div className="space-y-2">
                        <Label>Dia do mês</Label>
                        <Input type="number" min="1" max="31" value={recurDayOfMonth}
                          onChange={(e) => setRecurDayOfMonth(e.target.value)} />
                      </div>
                    )}
                    {recurFreq === 'every_n_days' && (
                      <div className="space-y-2">
                        <Label>A cada quantos dias</Label>
                        <Input type="number" min="1" value={recurIntervalDays}
                          onChange={(e) => setRecurIntervalDays(e.target.value)} />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Horário</Label>
                      <Input type="time" value={recurTime} onChange={(e) => setRecurTime(e.target.value)} />
                    </div>
                  </div>
                  {/* reserva fixa p/ a nota do clamp não empurrar o card */}
                  <div className="min-h-[16px]">
                    {recurFreq === 'monthly' && Number(recurDayOfMonth) > 28 && (
                      <p className="text-xs text-muted-foreground">
                        Em meses mais curtos, o disparo cai no último dia do mês.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Card: término — preenche a outra metade, com reserva p/ o campo condicional */}
              <div className="flex-1 rounded-xl border bg-card p-4 flex flex-col justify-start gap-4">
                <div className="space-y-2">
                  <Label>Término</Label>
                  <Select value={recurEndMode} onValueChange={setRecurEndMode}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Nunca (até eu pausar/excluir)</SelectItem>
                      <SelectItem value="on_date">Em uma data</SelectItem>
                      <SelectItem value="count">Após um nº de envios</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* espaço sempre reservado (rótulo + campo): aparece aqui sem fazer o layout pular */}
                <div className="min-h-[70px]">
                  {recurEndMode === 'on_date' && (
                    <div className="space-y-2">
                      <Label>Data</Label>
                      <Input type="date" value={recurEndDate}
                        onChange={(e) => setRecurEndDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)} />
                    </div>
                  )}
                  {recurEndMode === 'count' && (
                    <div className="space-y-2">
                      <Label>Número de envios</Label>
                      <Input type="number" min="1" value={recurMaxRuns}
                        onChange={(e) => setRecurMaxRuns(e.target.value)}
                        placeholder="Quantas vezes repetir" />
                    </div>
                  )}
                </div>
              </div>

              {/* Notificação por WhatsApp (mesma opção do modo "Uma vez", vale para cada disparo) */}
              <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="notifyByWhatsAppRecurring"
                    checked={notifyByWhatsApp}
                    onCheckedChange={(checked) => {
                      if (checked && (!user?.whatsapp || user.whatsapp.length < 10)) {
                        toast.error('Seu usuário não possui número de WhatsApp válido. Preencha em seu perfil.');
                        return;
                      }
                      setNotifyByWhatsApp(checked);
                      if (checked && user?.whatsapp) {
                        setNotifyWhatsAppNumber(user.whatsapp);
                      }
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-teal-600" />
                    <Label htmlFor="notifyByWhatsAppRecurring" className="text-sm font-normal cursor-pointer">
                      Receber notificação via WhatsApp a cada disparo
                    </Label>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center px-2">
                Cada disparo cria uma campanha própria no histórico. Os contatos do grupo são reavaliados a cada rodada. Horário no fuso de Brasília.
              </p>
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setScheduleDialogOpen(false);
              setNotifyByWhatsApp(false);
              setNotifyWhatsAppNumber('');
            }}>
              Cancelar
            </Button>
            {scheduleMode === 'once' ? (
              <Button onClick={handleScheduleClick} disabled={scheduling || !scheduleDateTime}>
                {scheduling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Agendando...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Agendar
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={handleCreateRecurring} disabled={creatingRecurring}>
                {creatingRecurring ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <Repeat className="w-4 h-4 mr-2" />
                    {(campaignToSchedule?.status === 'recurring' || campaignToSchedule?.status === 'recurring_done') ? 'Salvar' : 'Criar recorrência'}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Confirmation Dialog */}
      <Dialog open={scheduleConfirmDialogOpen} onOpenChange={setScheduleConfirmDialogOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-xl">Confirmar Agendamento</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-base text-muted-foreground">
              Tem certeza que deseja agendar a campanha <strong>{campaignToSchedule?.name}</strong>?
            </p>
            <p className="text-base text-muted-foreground whitespace-nowrap">
              Isso enviará mensagens para <strong>{campaignToSchedule?.total_contacts} contatos</strong> em{' '}
              <strong>
                {scheduleDateTime && new Date(scheduleDateTime).toLocaleString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </strong>.
            </p>
            
            {/* Advertência */}
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <div className="text-center mb-3">
                <span 
                  className={`text-3xl inline-block ${scheduleWarningPulsing ? 'animate-pulse' : ''}`}
                  style={scheduleWarningPulsing ? { 
                    animation: 'warningPulse 0.2s ease-in-out infinite',
                  } : {}}
                >⚠️</span>
                <span 
                  className="font-bold text-amber-800 ml-2 inline-block"
                  style={{ 
                    fontSize: '28px',
                    verticalAlign: '-2px',
                  }}
                >ATENÇÃO!</span>
              </div>
              <p className="text-base text-amber-700 text-center">
                O agendamento <strong>poderá ser cancelado</strong> antes do horário definido. Porém, uma vez que o envio se inicie, a campanha <strong>NÃO poderá ser interrompida</strong> devido à natureza do próprio aplicativo WhatsApp. 
                Todas as mensagens serão enviadas automaticamente.
              </p>
            </div>
            <style>{`
              @keyframes warningPulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.3); }
              }
            `}</style>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelScheduleConfirm}>
              Voltar
            </Button>
            <Button onClick={confirmSchedule} disabled={scheduling} className="bg-amber-600 hover:bg-amber-700">
              {scheduling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Agendando...
                </>
              ) : (
                'Confirmar e Agendar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fase D — "Ver envios": instâncias (disparos) já feitos por um molde recorrente */}
      <Dialog open={!!instancesMolde} onOpenChange={(open) => { if (!open) setInstancesMolde(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="w-5 h-5 text-foreground" />
              {/* leading-snug: o DialogTitle vem com leading-none e o truncate corta o pé
                  das letras g/j/p/ç; a entrelinha maior devolve o espaço do descendente */}
              <span className="truncate leading-snug">Envios de "{instancesMolde?.name}"</span>
            </DialogTitle>
          </DialogHeader>
          {instancesLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : instances.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Este agendamento ainda não teve nenhum envio.
            </div>
          ) : (
            <div className="flex gap-2">
              {/* Até 3 linhas visíveis; o excedente rola pelas setas (igual ao painel de recorrentes) */}
              <div ref={instancesScrollRef} className="flex-1 max-h-[232px] overflow-y-hidden space-y-2 pr-1">
                {instances.map((inst) => (
                  <div key={inst.id} className="h-[72px] rounded-lg border bg-card p-3 flex items-center justify-between gap-3 overflow-hidden">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate" title={inst.name}>{inst.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Clock className="w-3 h-3" />
                        {inst.created_at
                          ? new Date(inst.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '—'}
                      </div>
                      {inst.status === 'failed' && inst.error ? (
                        <div className="text-xs text-red-600 mt-0.5 flex items-center gap-1" title={inst.error}>
                          <AlertCircle className="w-3 h-3 shrink-0" />
                          <span className="truncate">{inst.error}</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {getStatusBadge(inst.status, inst)}
                      <div className="text-xs text-muted-foreground flex items-center gap-2 tabular-nums">
                        <span className="inline-flex items-center gap-1 text-teal-600" title="Enviadas">
                          <CheckCircle2 className="w-3 h-3" />{inst.sent_count}
                        </span>
                        {inst.failed_count > 0 && (
                          <span className="inline-flex items-center gap-1 text-red-600" title="Falhas">
                            <XCircle className="w-3 h-3" />{inst.failed_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Setas de rolagem — só quando passa de 3 linhas */}
              {instances.length > 3 && (
                <div className="flex flex-col justify-center gap-1 shrink-0">
                  <Button variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => instancesScrollRef.current?.scrollBy({ top: -80, behavior: 'smooth' })}>
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" className="h-8 w-8"
                    onClick={() => instancesScrollRef.current?.scrollBy({ top: 80, behavior: 'smooth' })}>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
